import { type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import { useCallback, useMemo, useState } from "react";

import type { PlansProcessRevisionsUploadFileV3 } from "@btnet/services-btnet/handlers/models/plansProcessRevisionsUploadFileV3";
import type { PlansProcessUploadRequest } from "@btnet/services-btnet/handlers/models/plansProcessUploadRequest";
import {
    plansProcessPostV2,
    plansRevisionsProcessPostV3,
} from "@btnet/services-btnet/handlers/plans/plans";

import { apixHandler } from "utilities/api/apix-handler";
import { reportError } from "utilities/errorHelpers";

import { type IPlanFileValidationResult, validatePlanFile } from "entity/Plans/utils/plans.utils";

import { type IPlanUploadItem } from "./PlansUploadModal.types";

export type UploadPhase = "idle" | "uploading" | "processing" | "done";

export interface IUploadResult {
    backgroundJobIds: string[];
    errors: Map<string, string>;
}

export interface IUsePlansUploadOptions {
    jobId: number;
}

export interface IUsePlansUploadResult {
    uploadFiles: (items: IPlanUploadItem[]) => Promise<IUploadResult>;
    progress: Map<string, number>;
    phase: UploadPhase;
    isUploading: boolean;
    validateFile: (file: File | BTFileSystem, validateType: boolean) => IPlanFileValidationResult;
    resetProgress: () => void;
}

export function usePlansUpload({ jobId }: IUsePlansUploadOptions): IUsePlansUploadResult {
    const [progress, setProgress] = useState<Map<string, number>>(new Map());
    const [isUploading, setIsUploading] = useState(false);

    const updateProgress = useCallback((itemId: string, percent: number) => {
        setProgress((prev) => {
            const next = new Map(prev);
            next.set(itemId, percent);
            return next;
        });
    }, []);

    const validateFile = useCallback((file: File | BTFileSystem, validateType: boolean) => {
        const fileName = file instanceof File ? file.name : file.fileName;
        const fileSize = file instanceof File ? file.size : file.fileSize;
        return validatePlanFile(fileName, fileSize, validateType);
    }, []);

    // Temp-uploads a computer file (or resolves a BT attachment) into the per-file shape the revisions
    // endpoint expects. Progress runs 0-90% for the transfer; the caller finishes it once the set's
    // process call returns.
    const resolveRevisionFile = useCallback(
        async (item: IPlanUploadItem): Promise<PlansProcessRevisionsUploadFileV3> => {
            updateProgress(item.id, 0);

            // A BT-sourced file is already in Buildertrend storage, so it skips the temp-upload step and
            // is referenced by its documentInstanceId.
            if (item.documentInstanceId != null) {
                updateProgress(item.id, 90);
                return { documentInstanceId: item.documentInstanceId };
            }

            if (!item.uploadFile?.originFileObj) {
                throw new Error(`Revisions upload requires a file for ${item.fileName}`);
            }

            const file = item.uploadFile.originFileObj;
            const tempUploadResponse = await apixHandler<PlansProcessUploadRequest>({
                url: "/apix/v2/Documents/temp-upload",
                method: "POST",
                headers: { "Content-Type": "application/octet-stream" },
                data: file,
                params: { fileName: file.name },
                transformRequest: [(data: unknown) => data],
                onUploadProgress: (progressEvent) => {
                    const total = progressEvent.total ?? file.size;
                    const percent = Math.round((progressEvent.loaded * 90) / total);
                    updateProgress(item.id, percent);
                },
            });

            updateProgress(item.id, 95);
            return {
                tempFilePath: tempUploadResponse.data.tempFilePath,
                fileName: tempUploadResponse.data.fileName,
                contentType: tempUploadResponse.data.contentType,
                fileLength: tempUploadResponse.data.fileLength,
            };
        },
        [updateProgress],
    );

    // Uploads all files targeting one existing set as a single revision. Per-set atomic: every file is
    // staged first, then one process call aggregates them into one review draft. A failure fails the
    // whole group (all its items get the error), matching the server's all-or-nothing behavior.
    const uploadRevisionGroup = useCallback(
        async (
            setId: string,
            items: IPlanUploadItem[],
        ): Promise<{ itemIds: string[]; backgroundJobId?: string }> => {
            const files = await Promise.all(items.map((item) => resolveRevisionFile(item)));

            const processResponse = await plansRevisionsProcessPostV3({ setId, files }, { jobId });

            items.forEach((item) => updateProgress(item.id, 100));
            return {
                itemIds: items.map((item) => item.id),
                backgroundJobId: processResponse.data.backgroundJobId,
            };
        },
        [jobId, resolveRevisionFile, updateProgress],
    );

    const uploadSingleItem = useCallback(
        async (item: IPlanUploadItem): Promise<{ itemId: string; backgroundJobId?: string }> => {
            updateProgress(item.id, 0);

            // Recipients to notify once a NEW set finishes processing.
            const notifyRecipientGlobalUserIds = item.notify
                ? (item.notifyRecipientGlobalUserIds ?? [])
                : [];

            // A BT-sourced file is already in Buildertrend storage, so it skips the temp-upload step and
            // is referenced by its documentInstanceId.
            if (item.documentInstanceId != null) {
                const processResponse = await plansProcessPostV2(
                    {
                        documentInstanceId: item.documentInstanceId,
                        notifyRecipientGlobalUserIds,
                    },
                    { jobId },
                );
                updateProgress(item.id, 100);
                return {
                    itemId: item.id,
                    backgroundJobId: processResponse.data.backgroundJobId,
                };
            }

            if (item.uploadFile?.originFileObj) {
                const file = item.uploadFile.originFileObj;
                const tempUploadResponse = await apixHandler<PlansProcessUploadRequest>({
                    url: "/apix/v2/Documents/temp-upload",
                    method: "POST",
                    headers: { "Content-Type": "application/octet-stream" },
                    data: file,
                    params: { fileName: file.name },
                    // File/Blob objects must bypass defaultTransformRequest, which JSON.stringify's all
                    // non-FormData payloads — serializing a File produces {} instead of binary content.
                    transformRequest: [(data: unknown) => data],
                    onUploadProgress: (progressEvent) => {
                        const total = progressEvent.total ?? file.size;
                        // Upload transfer is 0-90%, processing is the remaining 10%
                        const percent = Math.round((progressEvent.loaded * 90) / total);
                        updateProgress(item.id, percent);
                    },
                });

                updateProgress(item.id, 95);

                const processResponse = await plansProcessPostV2(
                    {
                        tempFilePath: tempUploadResponse.data.tempFilePath,
                        fileName: tempUploadResponse.data.fileName,
                        contentType: tempUploadResponse.data.contentType,
                        fileLength: tempUploadResponse.data.fileLength,
                        notifyRecipientGlobalUserIds,
                    },
                    { jobId },
                );

                updateProgress(item.id, 100);
                return {
                    itemId: item.id,
                    backgroundJobId: processResponse.data.backgroundJobId,
                };
            }

            throw new Error(`No valid file data for ${item.fileName}`);
        },
        [jobId, updateProgress],
    );

    const uploadFiles = useCallback(
        async (items: IPlanUploadItem[]): Promise<IUploadResult> => {
            setIsUploading(true);
            const backgroundJobIds: string[] = [];
            const errors = new Map<string, string>();

            try {
                // New-set files (no target set) upload one-per-call; revision files are grouped by their
                // target set so all documents for a set apply as one draft (multi-doc revisions).
                const newSetItems = items.filter((item) => !item.targetSetId);
                const revisionGroups = new Map<string, IPlanUploadItem[]>();
                items.forEach((item) => {
                    if (item.targetSetId) {
                        const group = revisionGroups.get(item.targetSetId) ?? [];
                        group.push(item);
                        revisionGroups.set(item.targetSetId, group);
                    }
                });

                // Run new-set and revision uploads concurrently — they're independent, and
                // Promise.allSettled never rejects, so the outer Promise.all is safe.
                const groupEntries = Array.from(revisionGroups.entries());
                const [newSetResults, revisionResults] = await Promise.all([
                    Promise.allSettled(newSetItems.map((item) => uploadSingleItem(item))),
                    Promise.allSettled(
                        groupEntries.map(([setId, groupItems]) =>
                            uploadRevisionGroup(setId, groupItems),
                        ),
                    ),
                ]);

                newSetResults.forEach((result, index) => {
                    const item = newSetItems[index];
                    if (result.status === "fulfilled") {
                        if (result.value.backgroundJobId) {
                            backgroundJobIds.push(result.value.backgroundJobId);
                        }
                    } else {
                        const error: unknown = result.reason;
                        const errorMessage =
                            error instanceof Error ? error.message : "Upload failed";
                        errors.set(item.id, errorMessage);

                        const fileType =
                            item.documentInstanceId != null ? "BT file" : "computer file";
                        reportError(error, {
                            internalNotes: `Plan upload failed for ${fileType}: ${item.fileName}, jobId: ${jobId}`,
                        });
                    }
                });

                revisionResults.forEach((result, index) => {
                    const [, groupItems] = groupEntries[index];
                    if (result.status === "fulfilled") {
                        if (result.value.backgroundJobId) {
                            backgroundJobIds.push(result.value.backgroundJobId);
                        }
                    } else {
                        const error: unknown = result.reason;
                        const errorMessage =
                            error instanceof Error ? error.message : "Upload failed";
                        // Per-set atomic: the whole group failed, so flag every file in it.
                        groupItems.forEach((item) => errors.set(item.id, errorMessage));
                        reportError(error, {
                            internalNotes: `Plan revision upload failed for set with ${groupItems.length} file(s), jobId: ${jobId}`,
                        });
                    }
                });

                return { backgroundJobIds, errors };
            } finally {
                setIsUploading(false);
            }
        },
        [uploadSingleItem, uploadRevisionGroup, jobId],
    );

    const phase = useMemo<UploadPhase>(() => {
        if (progress.size === 0) {
            return "idle";
        }
        const values = Array.from(progress.values());
        if (values.every((p) => p === 100)) {
            return isUploading ? "processing" : "done";
        }
        if (values.every((p) => p >= 90)) {
            return "processing";
        }
        return "uploading";
    }, [progress, isUploading]);

    const resetProgress = useCallback(() => {
        setProgress(new Map());
    }, []);

    return {
        uploadFiles,
        progress,
        phase,
        isUploading,
        validateFile,
        resetProgress,
    };
}
